import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  deleteTeacherArchived,
  deletePendingTeacher,
  getClassesByTeacherId,
  getTeacherAccountLookup,
  removeOrReplaceClassOwner,
  replaceClassCreator,
  shareClass,
  softDeleteTeacher
} from "../services/APIClient";
import { formatClassDisplayName, getClassGradeLabel } from "../utils/classDisplay";
import "./RemoveTeacherFromSchoolModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normaliseIdentityValue = (value) => normaliseText(value);

const getTeacherIdentityValues = (teacher) => [
  teacher?.id,
  teacher?._id,
  teacher?.teacherid,
  teacher?.teacherId,
  teacher?.teacher_id,
  teacher?.pendingteacherid,
  teacher?.pendingTeacherId,
  teacher?.pending_teacher_id,
  teacher?.userId,
  teacher?.user_id,
  teacher?.email
]
  .map(normaliseIdentityValue)
  .filter(Boolean);

const isSameTeacherRecord = (teacher, comparisonTeacher) => {
  const teacherValues = getTeacherIdentityValues(teacher);
  const comparisonValues = new Set(getTeacherIdentityValues(comparisonTeacher));

  if (teacherValues.some((value) => comparisonValues.has(value))) {
    return true;
  }

  return Boolean(
    normaliseText(teacher?.name) &&
    normaliseText(comparisonTeacher?.name) &&
    normaliseText(teacher?.name) === normaliseText(comparisonTeacher?.name) &&
    (teacher?.pendingteacherid || comparisonTeacher?.pendingteacherid)
  );
};

const isTeacherIdentityMatch = (teacher, identityValue) => {
  const target = normaliseIdentityValue(identityValue);
  return Boolean(target && getTeacherIdentityValues(teacher).includes(target));
};

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  secondTeacherName: String(
    prefill?.secondTeacherName ||
    prefill?.second_teacher_name ||
    ""
  ).trim()
});

const getLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.teachers)) return lookupResponse.teachers;
  if (Array.isArray(lookupResponse?.rows)) return lookupResponse.rows;
  if (Array.isArray(lookupResponse?.accounts)) return lookupResponse.accounts;
  return [];
};

const getTeacherRole = (teacher) => {
  const additionalRole = Array.isArray(teacher?.additionalRoles) ? teacher.additionalRoles[0] : "";
  return teacher?.roleLabel || teacher?.role || additionalRole || "Teacher";
};

const normaliseTeacher = (teacher) => {
  const pendingteacherid = teacher?.pendingteacherid || teacher?.pendingTeacherId || teacher?.pending_teacher_id || null;
  const id = pendingteacherid || teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId;
  const accountStatus = String(
    teacher?.accountStatus || teacher?.account_status || teacher?.userStatus || teacher?.status || ""
  ).trim() || (pendingteacherid ? "Invited" : "Active");

  return {
    ...teacher,
    id,
    pendingteacherid,
    name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
    email: teacher?.email || "",
    role: getTeacherRole(teacher),
    accountStatus
  };
};

const normaliseTeacherLookup = (lookupResponse) =>
  getLookupRows(lookupResponse)
    .map(normaliseTeacher)
    .filter((teacher) => teacher.id && teacher.active !== false)
    .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));

const getClassCreatorId = (classItem) =>
  classItem?.teacherId ||
  classItem?.teacher_id ||
  classItem?.teacherid ||
  classItem?.creatorId ||
  classItem?.creator_id ||
  classItem?.classCreatorId ||
  classItem?.class_creator_id;

const getClassOwnerId = (classItem) =>
  classItem?.classOwnerId ||
  classItem?.class_owner_id ||
  classItem?.classownerid ||
  classItem?.ownerId ||
  classItem?.owner_id;

const getClassTeacherIds = (classItem) =>
  Array.from(new Set(
    [getClassCreatorId(classItem), getClassOwnerId(classItem)]
      .map(normaliseIdentityValue)
      .filter(Boolean)
  ));

const normaliseClassAssociation = (item, countryGrades) => {
  const classItem = item?.class || item?.classItem || item;

  return {
    ...classItem,
    id: classItem?.id || classItem?._id,
    name: classItem?.name || classItem?.className || "Unnamed class",
    grade: getClassGradeLabel(classItem, countryGrades),
    teacherId: getClassCreatorId(classItem),
    classOwnerId: getClassOwnerId(classItem),
    association: String(item?.association || classItem?.association || "shared").toLowerCase()
  };
};

const mergeClassAssociations = (classes) => {
  const classesById = new Map();

  classes.forEach((classItem) => {
    const existingClass = classesById.get(classItem.id);

    if (!existingClass) {
      classesById.set(classItem.id, classItem);
      return;
    }

    const associations = new Set(
      `${existingClass.association},${classItem.association}`
        .split(",")
        .map((association) => association.trim())
        .filter(Boolean)
    );

    classesById.set(classItem.id, {
      ...existingClass,
      association: Array.from(associations).join(", ")
    });
  });

  return Array.from(classesById.values());
};

const getClassDisplayLabel = (classItem, countryGrades) =>
  formatClassDisplayName(classItem, classItem?.name || "Unnamed class", countryGrades);

const associationRequiresReplacement = (classItem) =>
  classItem.association.includes("owned") ||
  classItem.association.includes("created") ||
  classItem.association.includes("owner") ||
  classItem.association.includes("creator");

const requiresReplacement = (classItem, removedTeacher) => {
  const classTeacherIds = getClassTeacherIds(classItem);

  if (classTeacherIds.some((teacherId) => !isTeacherIdentityMatch(removedTeacher, teacherId))) {
    return false;
  }

  if (classTeacherIds.length > 0) {
    return classTeacherIds.some((teacherId) => isTeacherIdentityMatch(removedTeacher, teacherId));
  }

  return associationRequiresReplacement(classItem);
};

const findSingleExactMatch = (items, query, getValue) => {
  const target = normaliseText(query);

  if (!target) {
    return null;
  }

  const matches = items.filter((item) => normaliseText(getValue(item)) === target);
  return matches.length === 1 ? matches[0] : null;
};

function RemoveTeacherFromSchoolModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  currentTeacherId,
  currentTeacherName,
  prefill = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teachers, setTeachers] = useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedTeacherClasses, setSelectedTeacherClasses] = useState([]);
  const [replacementSearchTerms, setReplacementSearchTerms] = useState({});
  const [replacementByClassId, setReplacementByClassId] = useState({});
  const [removedTeacher, setRemovedTeacher] = useState(null);
  const [removedClasses, setRemovedClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const currentTeacherIdentity = useMemo(() => ({
    id: currentTeacherId,
    teacherid: currentTeacherId,
    name: currentTeacherName
  }), [currentTeacherId, currentTeacherName]);
  const removalTeachers = useMemo(
    () => teachers.filter((teacher) => !isSameTeacherRecord(teacher, currentTeacherIdentity)),
    [teachers, currentTeacherIdentity]
  );

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);
    if (!query) return [];

    return removalTeachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.role].some((value) => normaliseText(value).includes(query))
    );
  }, [teacherSearchTerm, removalTeachers]);

  const replacementClasses = useMemo(
    () => selectedTeacherClasses.filter((classItem) => requiresReplacement(classItem, selectedTeacher)),
    [selectedTeacherClasses, selectedTeacher]
  );

  const allReplacementClassesReady = replacementClasses.every(
    (classItem) => replacementByClassId[classItem.id]
  );

  const canContinue = Boolean(selectedTeacher && !isLoadingClasses);
  const canRemove = Boolean(selectedTeacher && (!replacementClasses.length || allReplacementClassesReady) && !isSubmitting);

  const loadTeachers = useCallback((prefillTeacherName = "") => {
    if (!schoolid) {
      setTeachers([]);
      setError("Teachers could not be loaded. Please try again.");
      return Promise.resolve();
    }

    setIsLoading(true);
    return getTeacherAccountLookup(schoolid)
      .then((lookupResponse) => {
        const normalisedTeachers = normaliseTeacherLookup(lookupResponse);

        setTeachers(normalisedTeachers);

        const prefilledTeacher = findSingleExactMatch(
          normalisedTeachers.filter((teacher) => !isSameTeacherRecord(teacher, currentTeacherIdentity)),
          prefillTeacherName,
          (teacher) => teacher.name
        );

        if (prefilledTeacher) {
          setSelectedTeacher(prefilledTeacher);
        }
      })
      .catch((loadError) => {
        console.error("Error loading teachers for removal:", loadError);
        setTeachers([]);
        setError("Teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [schoolid, currentTeacherIdentity]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeachers([]);
    setTeacherSearchTerm(prefillContext.teacherName);
    setSelectedTeacher(null);
    setSelectedTeacherClasses([]);
    setReplacementSearchTerms({});
    setReplacementByClassId({});
    setRemovedTeacher(null);
    setRemovedClasses([]);
    setError("");

    if (!schoolid) {
      setError("Teachers could not be loaded. Please try again.");
      return;
    }

    loadTeachers(prefillContext.teacherName);
  }, [show, schoolid, prefillContext.teacherName, loadTeachers]);

  useEffect(() => {
    if (!show || !selectedTeacher) {
      setSelectedTeacherClasses([]);
      return;
    }

    setIsLoadingClasses(true);
    setError("");
    getClassesByTeacherId(selectedTeacher.id)
      .then((classesResponse) => {
        const normalisedClasses = mergeClassAssociations(
          (Array.isArray(classesResponse) ? classesResponse : [])
            .map((classItem) => normaliseClassAssociation(classItem, countryGrades))
            .filter((classItem) => classItem.id)
        )
          .sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));

        setSelectedTeacherClasses(normalisedClasses);
      })
      .catch((classesError) => {
        console.error("Error loading teacher classes for removal:", classesError);
        setSelectedTeacherClasses([]);
        setError("This teacher's classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, selectedTeacher, countryGrades]);

  useEffect(() => {
    if (!show || !prefillContext.secondTeacherName || !replacementClasses.length) {
      return;
    }

    const prefilledReplacement = findSingleExactMatch(
      teachers.filter((teacher) => !isSameTeacherRecord(teacher, selectedTeacher)),
      prefillContext.secondTeacherName,
      (teacher) => teacher.name
    );

    if (!prefilledReplacement) {
      return;
    }

    setReplacementSearchTerms((currentTerms) => {
      const nextTerms = { ...currentTerms };
      replacementClasses.forEach((classItem) => {
        if (!nextTerms[classItem.id]) {
          nextTerms[classItem.id] = prefilledReplacement.name;
        }
      });
      return nextTerms;
    });
    setReplacementByClassId((currentReplacements) => {
      const nextReplacements = { ...currentReplacements };
      replacementClasses.forEach((classItem) => {
        if (!nextReplacements[classItem.id]) {
          nextReplacements[classItem.id] = prefilledReplacement;
        }
      });
      return nextReplacements;
    });
  }, [show, prefillContext.secondTeacherName, replacementClasses, selectedTeacher, teachers]);

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setReplacementSearchTerms({});
    setReplacementByClassId({});
    setError("");
  };

  const getReplacementOptions = (classId) => {
    const query = normaliseText(replacementSearchTerms[classId]);

    return teachers
      .filter((teacher) => !isSameTeacherRecord(teacher, selectedTeacher))
      .filter((teacher) => !query || [teacher.name, teacher.email].some((value) => normaliseText(value).includes(query)));
  };

  const assignReplacement = (classId, teacher) => {
    setReplacementByClassId((currentReplacements) => ({
      ...currentReplacements,
      [classId]: teacher
    }));
    setError("");
  };

  const goNext = () => {
    if (!canContinue) {
      return;
    }

    if (replacementClasses.length > 0) {
      setStage("replacement");
      return;
    }

    handleRemove();
  };

  const updateClassReplacement = async (classItem, replacementTeacher) => {
    if (classItem.association.includes("owned") || classItem.association.includes("owner")) {
      await removeOrReplaceClassOwner(classItem.id, replacementTeacher.id);
    }

    if (classItem.association.includes("created") || classItem.association.includes("creator")) {
      await replaceClassCreator(classItem.id, replacementTeacher.id);
    }

    if (!classItem.association.includes("owned") && !classItem.association.includes("created")) {
      await shareClass(classItem.id, replacementTeacher.id);
    }
  };

  const handleRemove = async () => {
    if (!canRemove) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await Promise.all(
        replacementClasses.map((classItem) =>
          updateClassReplacement(classItem, replacementByClassId[classItem.id])
        )
      );
      if (selectedTeacher.pendingteacherid) {
        await deletePendingTeacher(selectedTeacher.pendingteacherid);
      } else {
        await softDeleteTeacher(schoolid, selectedTeacher.id);
        await deleteTeacherArchived(schoolid, {
          teacherid: selectedTeacher.id,
          deletionReason: "Removed from school",
          deletedByTeacherId: currentTeacherId || "teacher-workflow",
          deletedByTeacherName: currentTeacherName || "Teacher workflow"
        });
      }
      setRemovedTeacher(selectedTeacher);
      setRemovedClasses(selectedTeacherClasses);
      setStage("success");
    } catch (submitError) {
      console.error("Error removing teacher from school:", submitError);
      setError("The teacher could not be removed from the school. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherTeacher = () => {
    setStage("form");
    setTeachers([]);
    setTeacherSearchTerm("");
    setSelectedTeacher(null);
    setSelectedTeacherClasses([]);
    setReplacementSearchTerms({});
    setReplacementByClassId({});
    setRemovedTeacher(null);
    setRemovedClasses([]);
    setError("");
    loadTeachers("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: removedTeacher?.email || removedTeacher?.name || "",
        message: "Teacher removal complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="remove-teacher-school-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearchTerm.trim()) {
      return <p className="remove-teacher-school-muted">Start typing a teacher name.</p>;
    }

    if (filteredTeachers.length < 1) {
      return <p className="remove-teacher-school-muted">No matching teachers found.</p>;
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`remove-teacher-school-result${selected ? " selected" : ""}`}
          onClick={() => handleTeacherSelect(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="remove-teacher-school-radio" aria-hidden="true" />
          <span className="remove-teacher-school-copy">
            <strong>{teacher.name}</strong>
            <span>{teacher.role}</span>
            <span className="remove-teacher-school-pill">
              {teacher.pendingteacherid
                ? (teacher.accountStatus || "Invited")
                : `Uses ${selected && isLoadingClasses ? "..." : selected ? selectedTeacherClasses.length : ""} classes`}
            </span>
          </span>
          {selected && <span className="remove-teacher-school-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderCurrentAccess = () => {
    if (!selectedTeacher) {
      return null;
    }

    if (isLoadingClasses) {
      return (
        <div className="remove-teacher-school-access">
          <Spinner animation="border" size="sm" />
          <span>Loading current school access...</span>
        </div>
      );
    }

    return (
      <div className="remove-teacher-school-access">
        <strong>Current school access</strong>
        <span className="remove-teacher-school-row">
          <span>Classes</span>
          <span>{selectedTeacherClasses.map((classItem) => getClassDisplayLabel(classItem, countryGrades)).join(", ") || "No classes found"}</span>
        </span>
      </div>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Remove Teacher From School</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-teacher-school-intro">Choose the teacher who should no longer use this school.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="removeTeacherFromSchoolSearch">
          <Form.Label>Which teacher should be removed?</Form.Label>
          <Form.Text as="div" className="remove-teacher-school-field-label">Teacher name</Form.Text>
          <Form.Control
            type="search"
            value={teacherSearchTerm}
            onChange={(event) => {
              setTeacherSearchTerm(event.target.value);
              setSelectedTeacher(null);
              setSelectedTeacherClasses([]);
              setError("");
            }}
            aria-label="Search teacher to remove"
          />
          <Form.Text className="text-muted">Choose from teachers currently in this school.</Form.Text>
        </Form.Group>
        <div className="remove-teacher-school-results">{renderTeacherResults()}</div>
        {renderCurrentAccess()}
        {selectedTeacher && (
          <div className="remove-teacher-school-note">
            {selectedTeacher.name} will be removed from this school. Classes, pupils, and results will stay unchanged.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-teacher-school-footer-note">
          {selectedTeacher ? `${selectedTeacher.name} selected.` : "Choose a teacher first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={goNext} disabled={!canContinue || isSubmitting}>
          {isSubmitting ? "Removing..." : replacementClasses.length > 0 ? "Next" : "Remove from school"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderReplacementClass = (classItem) => {
    const selectedReplacement = replacementByClassId[classItem.id];
    const options = getReplacementOptions(classItem.id);

    return (
      <div key={classItem.id} className="remove-teacher-school-replacement-card">
        <div className="remove-teacher-school-replacement-heading">
          <strong>{getClassDisplayLabel(classItem, countryGrades)}</strong>
          <span>{selectedTeacher?.name?.split(" ")[0]} is currently the only teacher</span>
        </div>
        <Form.Group controlId={`removeTeacherReplacement-${classItem.id}`}>
          <Form.Text as="div" className="remove-teacher-school-field-label">Teacher name</Form.Text>
          <Form.Control
            type="search"
            value={replacementSearchTerms[classItem.id] || ""}
            onChange={(event) => {
              setReplacementSearchTerms((currentTerms) => ({
                ...currentTerms,
                [classItem.id]: event.target.value
              }));
              setReplacementByClassId((currentReplacements) => {
                const nextReplacements = { ...currentReplacements };
                delete nextReplacements[classItem.id];
                return nextReplacements;
              });
            }}
          />
        </Form.Group>
        <div className="remove-teacher-school-results">
          {options.length < 1 ? (
            <p className="remove-teacher-school-muted">Start typing a teacher name.</p>
          ) : options.map((teacher) => {
            const selected = selectedReplacement?.id === teacher.id;

            return (
              <button
                key={`${classItem.id}-${teacher.id}`}
                type="button"
                className={`remove-teacher-school-result${selected ? " selected" : ""}`}
                onClick={() => assignReplacement(classItem.id, teacher)}
                aria-label={`Select ${teacher.name} for ${getClassDisplayLabel(classItem, countryGrades)}`}
                aria-pressed={selected}
              >
                <strong>{teacher.name}</strong>
                <span>Will be added to {getClassDisplayLabel(classItem, countryGrades)}.</span>
                {selected && <span className="remove-teacher-school-selected-label">Selected</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderReplacement = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Choose Teachers For Classes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-teacher-school-intro">Some classes need another teacher before {selectedTeacher?.name?.split(" ")[0]} is removed.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="remove-teacher-school-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>{replacementClasses.length} classes would have no teacher.</strong>
            <span>Add a teacher to each class before removing {selectedTeacher?.name?.split(" ")[0]} from the school.</span>
          </span>
        </Alert>
        <Form.Label>Select who should use these classes</Form.Label>
        <div className="remove-teacher-school-replacements">
          {replacementClasses.map(renderReplacementClass)}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-teacher-school-footer-note">
          {allReplacementClassesReady ? `${replacementClasses.length} classes ready.` : "Choose replacement teachers."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
        <Button variant="danger" onClick={handleRemove} disabled={!canRemove}>
          {isSubmitting ? "Removing..." : "Remove from school"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Teacher Removed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-teacher-school-intro">School has been updated.</p>
        <Alert variant="success" className="remove-teacher-school-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{removedTeacher?.name} can no longer use this school.</strong>
            <span>Classes, pupils, assessment results, and notes were kept.</span>
          </span>
        </Alert>
        <div className="remove-teacher-school-summary">
          <strong>What happened</strong>
          <span className="remove-teacher-school-row">
            <span>Removed</span>
            <span>{removedTeacher?.name} from this school</span>
          </span>
          <span className="remove-teacher-school-row">
            <span>Class access</span>
            <span>{removedTeacher?.name?.split(" ")[0]} was removed from {removedClasses.map((classItem) => getClassDisplayLabel(classItem, countryGrades)).join(", ") || "school classes"}.</span>
          </span>
          <span className="remove-teacher-school-row">
            <span>Kept</span>
            <span>Classes, pupils, assessment results, notes, and school history.</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-teacher-school-footer-note">
          {embedded ? "Done returns to SENSAI." : "School updated."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnotherTeacher}>Remove another teacher</Button>
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="remove-teacher-school-modal"
    >
      {stage === "success" ? renderSuccess() : stage === "replacement" ? renderReplacement() : renderForm()}
    </Modal>
  );
}

export default RemoveTeacherFromSchoolModal;
